import { Link } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";
import compassLogo from "@/assets/logo-footer.webp";

import { AGENCY } from "@/lib/agency";
import { featuredLoanProducts } from "@/lib/loan-products";
import { serviceAreas } from "@/lib/service-areas";

const eyebrow = "text-xs font-semibold uppercase tracking-[0.18em]";

export function SiteFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link
              to="/"
              className="inline-flex items-center"
              aria-label={AGENCY.name}
            >
              <img
                src={compassLogo}
                alt={AGENCY.name}
                width={500}
                height={167}
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-primary-foreground/75">
              An independent business financing advisory arranging working
              capital and specialty loans for {AGENCY.area}.
            </p>
            <div className="mt-5 space-y-2 text-sm text-primary-foreground/80">
              <p className="flex items-start gap-2">
                <Icon name="mappin" color="bl" className="mt-0.5 h-4 w-4 shrink-0 brightness-0 invert"  />
                <span>{AGENCY.address}</span>
              </p>
              <p>
                <a
                  href={AGENCY.phoneHref}
                  className="font-semibold text-accent hover:underline"
                >
                  {AGENCY.phone}
                </a>
              </p>
              <p className="text-primary-foreground/65">
                Mon-Fri · 8:00 AM - 7:00 PM CT
                <br />
                Sat · 9:00 AM - 2:00 PM CT
              </p>
            </div>
          </div>

          {/* Loans */}
          <div>
            <p className={`${eyebrow} text-accent`}>Loans</p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {featuredLoanProducts.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/dallas/$slug"
                    params={{ slug: p.slug }}
                    className="text-primary-foreground/80 transition hover:text-accent"
                  >
                    {p.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/dallas"
                  className="font-semibold text-primary-foreground transition hover:text-accent"
                >
                  All Dallas programs →
                </Link>
              </li>
            </ul>
          </div>

          {/* Service areas */}
          <div>
            <p className={`${eyebrow} text-accent`}>Service areas</p>
            <ul className="mt-5 grid grid-cols-1 gap-2.5 text-sm sm:grid-cols-2 lg:grid-cols-1">
              {serviceAreas.slice(0, 6).map((area) => (
                <li key={area.slug}>
                  <a
                    href={`/service-areas/${area.slug}`}
                    className="text-primary-foreground/80 transition hover:text-accent"
                  >
                    {area.city}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/service-areas"
                  className="font-semibold text-primary-foreground transition hover:text-accent"
                >
                  All service areas →
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className={`${eyebrow} text-accent`}>Company</p>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li><a href="/about" className="text-primary-foreground/80 transition hover:text-accent">About</a></li>
              <li><a href="/contact" className="text-primary-foreground/80 transition hover:text-accent">Contact</a></li>
              <li><a href="/service-areas" className="text-primary-foreground/80 transition hover:text-accent">Service Areas</a></li>
              <li><Link to="/dallas" className="text-primary-foreground/80 transition hover:text-accent">Dallas Loans</Link></li>
            </ul>
          </div>
        </div>

        {/* Legal */}
        <div className="mt-14 border-t border-primary-foreground/15 pt-8 text-xs leading-relaxed text-primary-foreground/60">
          <p>
            Content reviewed by the {AGENCY.short} advisory team. All loans are
            subject to lender approval and underwriting. {AGENCY.name} is a
            business financing advisory headquartered in Dallas, TX, and is not
            a direct lender. Rates, terms, fees, and product availability vary
            by lender and depend on the borrower's business profile, credit,
            time in business, and revenue.
          </p>
          <div className="mt-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <p className="text-primary-foreground/55">
              © {year} {AGENCY.name}. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              <a href="/privacy" className="hover:text-accent">Privacy</a>
              <a href="/terms" className="hover:text-accent">Terms</a>
              <a href="/disclosures" className="hover:text-accent">Disclosures</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
